import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { http } from "@/lib/api";

const CartCtx = createContext(null);
const STORAGE_KEY = "bls_cart_v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState([]); // [{product_id, name, price, image, quantity}]
  const [totals, setTotals] = useState({ subtotal: 0, shipping: 0, tax: 0, total: 0, currency_symbol: "₹" });
  const [open, setOpen] = useState(false);

  // Load from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setItems(JSON.parse(raw));
    } catch (_e) {
      // ignore parse errors
    }
  }, []);

  // Persist
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  // Recalc totals from server when items change
  const recalc = useCallback(async () => {
    if (items.length === 0) {
      setTotals({ subtotal: 0, shipping: 0, tax: 0, total: 0, currency_symbol: "₹" });
      return;
    }
    try {
      const { data } = await http.post(
        "/cart/totals",
        items.map((i) => ({ product_id: i.product_id, quantity: i.quantity }))
      );
      setTotals(data);
      // Replace local items with server-validated lines (keeps current price/name)
      setItems((prev) =>
        data.items.map((d) => ({
          product_id: d.product_id,
          name: d.name,
          image: d.image,
          price: d.price,
          quantity: d.quantity,
        }))
      );
    } catch (_) {
      // ignore
    }
  }, [items.length]);

  useEffect(() => {
    recalc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  const add = (product, qty = 1) => {
    setItems((prev) => {
      const exists = prev.find((p) => p.product_id === product.id);
      if (exists) {
        return prev.map((p) =>
          p.product_id === product.id ? { ...p, quantity: p.quantity + qty } : p
        );
      }
      return [
        ...prev,
        {
          product_id: product.id,
          name: product.name,
          price: product.price,
          image: (product.images && product.images[0]) || null,
          quantity: qty,
        },
      ];
    });
    setOpen(true);
  };

  const update = (product_id, quantity) => {
    setItems((prev) =>
      prev
        .map((p) => (p.product_id === product_id ? { ...p, quantity: Math.max(0, quantity) } : p))
        .filter((p) => p.quantity > 0)
    );
  };

  const remove = (product_id) => {
    setItems((prev) => prev.filter((p) => p.product_id !== product_id));
  };

  const clear = () => {
    setItems([]);
  };

  const count = items.reduce((s, i) => s + i.quantity, 0);

  return (
    <CartCtx.Provider value={{ items, totals, count, add, update, remove, clear, open, setOpen, recalc }}>
      {children}
    </CartCtx.Provider>
  );
}

export const useCart = () => useContext(CartCtx);
